import React, { useState } from 'react';
import styled from 'styled-components';
import { FiMessageSquare, FiX, FiSend, FiZap, FiLoader } from 'react-icons/fi';

const AIContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
`;

const AIButton = styled.button`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const AIPanel = styled.div`
  position: absolute;
  bottom: 4.5rem;
  right: 0;
  width: 350px;
  height: 450px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 1rem;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-family: ${props => props.theme.fonts.accent};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 50%;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Message = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  max-width: 80%;
  font-size: 0.9rem;
  line-height: 1.4;
  
  ${props => props.isUser ? `
    align-self: flex-end;
    background-color: ${props.theme.colors.primary};
    color: white;
  ` : `
    align-self: flex-start;
    background-color: ${props.theme.colors.background};
    color: ${props.theme.colors.text};
  `}
`;

const InputContainer = styled.div`
  padding: 1rem;
  border-top: 1px solid ${props => props.theme.colors.divider};
  display: flex;
  gap: 0.5rem;
`;

const Input = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.body};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SendButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  width: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.divider};
    cursor: not-allowed;
  }
`;

const SuggestionsContainer = styled.div`
  padding: 0.5rem 1rem;
  border-top: 1px solid ${props => props.theme.colors.divider};
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const SuggestionButton = styled.button`
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 16px;
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  color: ${props => props.theme.colors.primary};
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AIAssistant = ({ articleData, onSuggestionApply }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { text: 'Hi there! I\'m your AI writing assistant. How can I help with your article today?', isUser: false }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  const suggestions = [
    { text: 'Improve headline', action: 'headline' },
    { text: 'Generate intro', action: 'intro' },
    { text: 'Suggest quotes', action: 'quotes' },
    { text: 'Find statistics', action: 'stats' },
    { text: 'Improve clarity', action: 'clarity' }
  ];

  const handleSend = () => {
    if (!input.trim()) return;
    
    // Add user message
    const userMessage = { text: input, isUser: true };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    // Simulate AI response
    setTimeout(() => {
      let response;
      
      // Simple keyword-based responses for demo
      if (input.toLowerCase().includes('headline') || input.toLowerCase().includes('title')) {
        response = { 
          text: `Here are some headline suggestions for your article about ${articleData?.section || 'this topic'}:\n\n1. "${generateHeadline(articleData?.section)}"\n2. "${generateHeadline(articleData?.section)}"\n3. "${generateHeadline(articleData?.section)}"`, 
          isUser: false 
        };
      } else if (input.toLowerCase().includes('intro') || input.toLowerCase().includes('introduction')) {
        response = { 
          text: generateIntro(articleData?.section), 
          isUser: false 
        };
      } else if (input.toLowerCase().includes('quote') || input.toLowerCase().includes('quotes')) {
        response = { 
          text: generateQuotes(articleData?.section), 
          isUser: false 
        };
      } else {
        response = { 
          text: 'I understand you need help with your article. Could you be more specific about what you need? You can ask me to help with headlines, introductions, quotes, or general writing advice.', 
          isUser: false 
        };
      }
      
      setMessages(prev => [...prev, response]);
      setLoading(false);
    }, 1500);
  };

  const handleSuggestion = (action) => {
    let suggestionPrompt = '';
    
    switch(action) {
      case 'headline':
        suggestionPrompt = 'Can you suggest some catchy headlines for my article?';
        break;
      case 'intro':
        suggestionPrompt = 'Help me write an engaging introduction for this article.';
        break;
      case 'quotes':
        suggestionPrompt = 'Suggest some quotes I could include in my article.';
        break;
      case 'stats':
        suggestionPrompt = 'What statistics might be relevant for this article?';
        break;
      case 'clarity':
        suggestionPrompt = 'How can I make my writing more clear and engaging?';
        break;
      default:
        suggestionPrompt = 'Can you help me with my article?';
    }
    
    setInput(suggestionPrompt);
    setIsOpen(true);
  };

  // Helper functions to generate content based on section
  const generateHeadline = (section = 'news') => {
    const headlines = {
      news: [
        'Breaking: School Board Approves New Budget',
        'Students Lead Initiative for Campus Sustainability',
        'New Policy Aims to Reduce Homework Stress'
      ],
      sports: [
        'Spartans Clinch Championship in Thrilling Overtime Victory',
        'Star Quarterback Breaks School Record in Season Opener',
        'Track Team Qualifies Six Athletes for State Finals'
      ],
      features: [
        'Behind the Scenes: The Making of This Year\'s School Musical',
        'Teacher Spotlight: Ms. Rodriguez\'s Journey from Student to Educator',
        'How the Robotics Club Built Their Award-Winning Design'
      ],
      opinion: [
        'Editorial: Why School Spirit Matters More Than Ever',
        'Commentary: The Case for Later School Start Times',
        'Perspective: What Student Government Gets Right (and Wrong)'
      ]
    };
    
    const sectionHeadlines = headlines[section] || headlines.news;
    return sectionHeadlines[Math.floor(Math.random() * sectionHeadlines.length)];
  };

  const generateIntro = (section = 'news') => {
    const intros = {
      news: 'In a development that could significantly impact students and faculty, the school administration announced yesterday that [key information]. This decision comes after months of deliberation and community input, with supporters highlighting [benefit] while critics express concerns about [potential issue].',
      sports: 'The atmosphere was electric in the gymnasium last Friday as the Schurr High Spartans faced off against their long-time rivals in a match that would determine [stakes]. With [context about season/standings], both teams knew the importance of securing a victory.',
      features: 'Behind every successful [program/event/person] at Schurr High is a story of dedication, creativity, and perseverance. For [subject], the journey began [timeframe] ago when [initial situation]. Since then, [development] has transformed not only [specific area] but also the broader school community.',
      opinion: 'As students navigate the complexities of high school life, the issue of [topic] continues to generate significant debate. While the administration has implemented [current policy/approach], many in our community believe it\'s time to reconsider our approach to this important matter.'
    };
    
    return intros[section] || intros.news;
  };

  const generateQuotes = (section = 'news') => {
    const quotes = {
      news: '"This initiative represents our commitment to [value/goal]," said Principal [Name]. "We believe it will create opportunities for students to [benefit]."\n\nSophomore Maria Chen expressed enthusiasm: "As someone who has been advocating for [related issue], I\'m excited to see the administration taking concrete steps."',
      sports: '"We\'ve been preparing for this moment all season," Coach Williams explained after the game. "The team showed incredible heart and resilience when it mattered most."\n\nTeam captain James Rodriguez added, "This win wasn\'t just about talent—it was about trust and teamwork. We believed in each other even when we were down."',
      features: '"What makes this program special is the sense of community," explained [Name], who has [role] for [timeframe]. "Students discover not just skills but also parts of themselves they never knew existed."\n\n"At first, I was intimidated by the challenge," admitted junior Alex Nguyen. "But the support from [mentors/peers] helped me push beyond my comfort zone."',
      opinion: '"The current approach to [issue] fails to consider the perspective of [stakeholder group]," argues Student Body President Sophia Martinez. "We need a more inclusive process for making these decisions."\n\nFaculty member Mr. Thompson offers a different view: "While I understand the concerns, we must also consider practical limitations such as [constraint]."'
    };
    
    return quotes[section] || quotes.news;
  };

  return (
    <AIContainer>
      {isOpen && (
        <AIPanel>
          <PanelHeader>
            <PanelTitle>AI Writing Assistant</PanelTitle>
            <CloseButton onClick={() => setIsOpen(false)}>
              <FiX size={18} />
            </CloseButton>
          </PanelHeader>
          
          <ChatContainer>
            {messages.map((message, index) => (
              <Message key={index} isUser={message.isUser}>
                {message.text}
              </Message>
            ))}
            
            {loading && (
              <LoadingIndicator>
                <FiLoader size={24} />
              </LoadingIndicator>
            )}
          </ChatContainer>
          
          <SuggestionsContainer>
            {suggestions.map((suggestion, index) => (
              <SuggestionButton 
                key={index} 
                onClick={() => handleSuggestion(suggestion.action)}
              >
                <FiZap size={14} /> {suggestion.text}
              </SuggestionButton>
            ))}
          </SuggestionsContainer>
          
          <InputContainer>
            <Input 
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask for writing help..."
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            />
            <SendButton onClick={handleSend} disabled={!input.trim() || loading}>
              <FiSend size={18} />
            </SendButton>
          </InputContainer>
        </AIPanel>
      )}
      
      <AIButton onClick={() => setIsOpen(!isOpen)}>
        <FiMessageSquare size={24} />
      </AIButton>
    </AIContainer>
  );
};

export default AIAssistant;